import { useCallback, useEffect, useMemo, useState } from 'react';
import { pmaImportAPI } from '../services/api';

export default function usePmaStudentPicker() {
  const [query, setQuery] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedById, setSelectedById] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadStudents = useCallback(async (q) => {
    setLoading(true);
    setError('');
    try {
      const res = await pmaImportAPI.listStudents({ q: q || undefined, take: 80 });
      const list = res.data.students || [];
      setStudents(list);
      setSelectedById((prev) => {
        const next = { ...prev };
        for (const student of list) {
          if (next[student.id]) next[student.id] = student;
        }
        return next;
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load students');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => loadStudents(query.trim()), query ? 300 : 0);
    return () => clearTimeout(handle);
  }, [query, loadStudents]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const selectedStudents = useMemo(
    () => selectedIds.map((id) => selectedById[id]).filter(Boolean),
    [selectedIds, selectedById]
  );

  const toggleStudent = useCallback((id, student) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        setSelectedById((map) => {
          const next = { ...map };
          delete next[id];
          return next;
        });
        return prev.filter((item) => item !== id);
      }
      if (student) {
        setSelectedById((map) => ({ ...map, [id]: student }));
      }
      return [...prev, id];
    });
  }, []);

  const toggleVisible = useCallback(() => {
    setSelectedIds((prev) => {
      const visibleIds = students.map((s) => s.id);
      const allVisibleSelected = visibleIds.every((id) => prev.includes(id));
      if (allVisibleSelected) {
        setSelectedById((map) => {
          const next = { ...map };
          for (const id of visibleIds) delete next[id];
          return next;
        });
        return prev.filter((id) => !visibleIds.includes(id));
      }
      setSelectedById((map) => {
        const next = { ...map };
        for (const student of students) next[student.id] = student;
        return next;
      });
      return [...new Set([...prev, ...visibleIds])];
    });
  }, [students]);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setSelectedById({});
  }, []);

  return {
    query,
    setQuery,
    students,
    selectedIds,
    selectedStudents,
    selectedCount: selectedIds.length,
    selectedSet,
    loading,
    error,
    toggleStudent,
    toggleVisible,
    clearSelection,
    reload: () => loadStudents(query.trim()),
  };
}
